import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SelectedPanelEnum } from '@app/core';
import { InsurancesQueryParamsInterface } from '@app/my-zone/interfaces/insurances-query-params.interface';
import { PremiumImpactService } from '@app/my-zone/shared/premium-impact-modal';
import { PanelExpandableComponent } from '@app/shared/ux/panel-expandable/panel-expandable.component';
import { MinimumAgeValidator } from '@inshared/shared/util-validators';
import {
    DateService,
    ElementValidator,
    ErrorInterface,
    GenderCodeEnum,
    InsurancePolicyActionService,
    PolicyProductIdEnum, ProductRegularDriverResponseInterface,
    PurchasedProductInterface,
    RelationCodeEnum,
} from 'outshared-lib';

const BIRTHDATE_VALIDATOR = {
    NAME: 'birthdate',
    WEBSERVICE_ID: 'VERZ.PROD.VSLG.AFBT',
    CONTEXT: 'birthdate',
};

@Component({
    selector: 'ins-insurance-deviant-driver-form',
    templateUrl: './insurance-deviant-driver-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsuranceDeviantDriverFormComponent implements OnInit {

    @Input() public purchasedProduct!: PurchasedProductInterface;
    @Input() public relationCode!: RelationCodeEnum;

    @ViewChild(PanelExpandableComponent, { static: false }) panel: PanelExpandableComponent;

    public isDisabled: boolean;
    public form: UntypedFormGroup;
    public minimumStartDate: string;
    public productIdEnum: typeof PolicyProductIdEnum;
    public errors: ErrorInterface[];
    public genderCodeEnum: typeof GenderCodeEnum;
    public minimumAges = {
        [PolicyProductIdEnum.Car]: 17,
        [PolicyProductIdEnum.Moped]: 16,
        [PolicyProductIdEnum.Bicycle]: 12,
    };

    constructor(
        private dateService: DateService,
        private minimumAgeValidator: MinimumAgeValidator,
        private elementValidator: ElementValidator,
        private insurancePolicyActionService: InsurancePolicyActionService,
        private router: Router,
        private changeDetectorRef: ChangeDetectorRef,
        private premiumImpactService: PremiumImpactService,
    ) {
        this.isDisabled = false;
        this.productIdEnum = PolicyProductIdEnum;
        this.errors = [];
        this.genderCodeEnum = GenderCodeEnum;
    }

    public get initials(): AbstractControl {
        return this.form.get('initials');
    }

    public get middleNameInitials(): AbstractControl {
        return this.form.get('middleNameInitials');
    }

    public get surname(): AbstractControl {
        return this.form.get('surname');
    }

    public get birthdate(): AbstractControl {
        return this.form.get('birthdate');
    }

    public get startDate(): AbstractControl {
        return this.form.get('startDate');
    }

    public get genderCode(): AbstractControl {
        return this.form.get('genderCode');
    }

    public get minimumAge(): string {
        return this.dateService.subtract(this.dateService.add(1, 'day'), this.getMinimumAge(), 'year', 'YYYY-MM-DD');
    }

    public ngOnInit(): void {
        this.minimumStartDate = this.dateService.add(1, 'day');
        const regularDriver = this.purchasedProduct?.productDetails?.regularDriver;
        this.form = this.createForm(this.minimumAge, regularDriver);

        this.resetForm(regularDriver);
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        this.insurancePolicyActionService
            .addRegularDriver$({
                birthdate: this.birthdate.value,
                genderCode: this.genderCode.value,
                initials: this.initials.value,
                middleNameInitials: this.middleNameInitials.value,
                productId: this.purchasedProduct.productId,
                productSequenceNumber: this.purchasedProduct.productSequenceNumber,
                relationCode: this.relationCode,
                startDate: this.startDate.value,
                surname: this.surname.value,
            })
            .subscribe({
                next: () => this.onAddRegularDriverSuccess(),
                error: (errors) => this.onAddRegularDriverError(errors),
            });

        this.isDisabled = true;
        this.errors = [];
    }

    private onAddRegularDriverSuccess(): void {
        this.isDisabled = false;
        this.errors = [];
        this.closePanel();
        this.onGetChanges(this.startDate.value, this.purchasedProduct.productSequenceNumber);
        this.changeDetectorRef.detectChanges();
    }

    private closePanel(): void {
        const queryParams: InsurancesQueryParamsInterface = {
            panel: SelectedPanelEnum.Close,
            productId: this.purchasedProduct.productId,
            productSequenceNumber: this.purchasedProduct.productSequenceNumber,
            scroll: true,
        };

        this.router.navigate([], {
            queryParams,
            queryParamsHandling: 'merge',
        });
    }

    private onGetChanges(referenceDate: string, sequenceNumber: number): void {
        this.premiumImpactService
            .getChanges(referenceDate, sequenceNumber)
            .subscribe();
    }

    private onAddRegularDriverError(errors: ErrorInterface[]): void {
        this.errors = errors;
        this.isDisabled = false;
        this.changeDetectorRef.detectChanges();
    }

    private createForm(minimumAge: string, regularDriver: ProductRegularDriverResponseInterface): FormGroup {
        const { surname, birthdate, genderCode, middleNameInitials, initials } = regularDriver || {};
        return new UntypedFormGroup({
            surname: new FormControl(surname ?? '', [Validators.required]),
            birthdate: new FormControl(birthdate ?? '', {
                validators: [
                    Validators.required,
                    this.minimumAgeValidator.validate(minimumAge),
                ],
                asyncValidators: [
                    this.elementValidator.validate(
                        BIRTHDATE_VALIDATOR.NAME,
                        BIRTHDATE_VALIDATOR.WEBSERVICE_ID,
                        BIRTHDATE_VALIDATOR.CONTEXT,
                    ),
                ],
                updateOn: 'blur',
            }),
            genderCode: new FormControl(genderCode ?? '', [Validators.required]),
            startDate: new FormControl('', [Validators.required]),
            middleNameInitials: new FormControl(middleNameInitials ?? ''),
            initials: new FormControl(initials ?? '', [Validators.required]),
        });
    }

    private resetForm(regularDriver: ProductRegularDriverResponseInterface): void {
        if (!regularDriver) {
            return;
        }

        if (regularDriver.relationCode === this.relationCode) {
            this.form.reset({
                surname: regularDriver.surname,
                birthdate: regularDriver.birthdate,
                genderCode: regularDriver.genderCode,
                startDate: '',
                middleNameInitials: regularDriver.middleNameInitials,
                initials: regularDriver.initials,
            });
        } else {
            this.form.reset();
        }
    }

    public getMinimumAge(): number {
        return this.minimumAges[this.purchasedProduct.productId] || 17;
    }
}
